// Folds crypto rows into one line per underlying asset.
//
// The same exposure arrives as several rows: SOL in five wallets, ETH on three
// chains, and ETH again as WETH, wstETH and an Aave receipt. The table shows
// one row per ASSET, and the per-wallet, per-wrapper rows as its breakdown.
//
// Wrappers are listed, not guessed from a leading "W" or a trailing "ETH":
// WIF is not wrapped IF, and JTO is Jito's governance token, not staked SOL.

const ASSET = {
    // Wrapped and liquid-staked ETH.
    WETH: 'ETH',
    STETH: 'ETH',
    WSTETH: 'ETH',
    CBETH: 'ETH',
    RETH: 'ETH',
    EETH: 'ETH',
    WEETH: 'ETH',
    METH: 'ETH',
    CMETH: 'ETH',
    SWETH: 'ETH',
    OSETH: 'ETH',
    ETHX: 'ETH',
    FRXETH: 'ETH',
    SFRXETH: 'ETH',
    EZETH: 'ETH',
    RSETH: 'ETH',
    WRSETH: 'ETH',
    SUPEROETHB: 'ETH',
    // Wrapped and liquid-staked SOL.
    WSOL: 'SOL',
    MSOL: 'SOL',
    JITOSOL: 'SOL',
    BSOL: 'SOL',
    JUPSOL: 'SOL',
    INF: 'SOL',
    STSOL: 'SOL',
    HSOL: 'SOL',
    DSOL: 'SOL',
    VSOL: 'SOL',
    BNSOL: 'SOL',
    BBSOL: 'SOL',
    // Wrapped and bridged BTC, Hyperliquid's Unit tokens among them.
    WBTC: 'BTC',
    CBBTC: 'BTC',
    UBTC: 'BTC',
    // Hyperliquid's Unit-bridged ETH and SOL.
    UETH: 'ETH',
    USOL: 'SOL',
    // Other wrappers held.
    WTAO: 'TAO',
    // A provider that reports the name where the ticker belongs.
    'SOLANA NAME SERVICE': 'SNS',
};

// Aave receipt tokens: a chain prefix on the asset lent (aBasWETH, aArbARB).
const AAVE = /^A(ETH|ARB|BAS|OPT|POL|AVA|SCR|ZKS|LIN)(.+)$/;

export const upperSymbol = (symbol) => String(symbol ?? '').trim().toUpperCase();

/** The asset a token is a claim on, as an upper-case symbol. */
export function assetOf(symbol) {
    const s = upperSymbol(symbol);
    if (ASSET[s]) return ASSET[s];
    const aave = AAVE.exec(s);
    if (aave) return ASSET[aave[2]] ?? aave[2];
    return s;
}

const sum = (rows, key) => rows.reduce((total, r) => total + r[key], 0);
const known = (rows, key) => rows.every((r) => r[key] !== null && r[key] !== undefined);
const extreme = (rows, key, pick) => {
    const dates = rows.map((r) => r[key]).filter(Boolean);
    return dates.length ? dates.reduce((a, b) => (pick(new Date(a), new Date(b)) ? a : b)) : null;
};
const shared = (rows, key) => (rows.every((r) => r[key] === rows[0][key]) ? rows[0][key] : null);

/**
 * Groups rows by asset. Each group is a row in the table's own shape, plus
 * `members` (the rows it summarises).
 *
 * Quantity is in units of the asset. Where every member is the same token that
 * is their sum; where they are different tokens (ETH and wstETH are not one
 * unit) it is the value at the plain asset's price, and a dash when no member
 * quotes one. Averages and P/L are dashes unless every member's cost is known
 * — a partial sum would understate the cost and overstate the gain.
 */
export function groupByAsset(rows) {
    const byAsset = new Map();
    for (const row of rows) {
        const r = { ...row, symbol: upperSymbol(row.symbol) };
        const key = assetOf(r.symbol);
        if (!byAsset.has(key)) byAsset.set(key, []);
        byAsset.get(key).push(r);
    }

    return [...byAsset].map(([asset, members]) => {
        if (members.length === 1) return { ...members[0], asset, members };

        const value = sum(members, 'value');
        const sameToken = members.every((m) => m.symbol === members[0].symbol);
        const plain = members.find((m) => m.symbol === asset && m.currentPrice);
        const currentPrice = sameToken
            ? (members.every((m) => m.quantity) ? value / sum(members, 'quantity') : null)
            : plain?.currentPrice ?? null;
        const quantity = sameToken
            ? sum(members, 'quantity')
            : currentPrice ? value / currentPrice : null;

        // The logo and link of the plain asset where it is held, or of the
        // first member that has one.
        const face = [plain ?? members.find((m) => m.symbol === asset), ...members]
            .find((m) => m && (m.logoUrl || m.coingeckoUrl || m.dexscreenerUrl)) ?? {};

        const costed = known(members, 'costBasis');
        const costBasis = costed ? sum(members, 'costBasis') : null;
        const pnl = costed ? value - costBasis : null;

        return {
            asset,
            members,
            walletId: shared(members, 'walletId'),
            chain: shared(members, 'chain'),
            token: `group:${asset}`,
            symbol: asset,
            name: null,
            // One label for the group only when every member shares it.
            kind: shared(members, 'kind'),
            protocol: shared(members, 'protocol'),
            logoUrl: face.logoUrl ?? null,
            coingeckoUrl: face.coingeckoUrl ?? null,
            dexscreenerUrl: face.dexscreenerUrl ?? null,
            quantity,
            currentPrice,
            value,
            costBasis,
            avgEntryPrice: costed && quantity ? costBasis / quantity : null,
            pnl,
            pnlPct: costed && costBasis > 0 ? (pnl / costBasis) * 100 : null,
            firstPurchaseAt: extreme(members, 'firstPurchaseAt', (a, b) => a < b),
            lastPurchaseAt: extreme(members, 'lastPurchaseAt', (a, b) => a > b),
        };
    });
}
